/* eslint-disable react/no-danger */
/* eslint-disable camelcase */

// node modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { connect } from 'react-redux';
import NoSsr from '@material-ui/core/NoSsr';

// app modules: actions
import { openModal } from 'actions/modals';

// app modules: components
import Heading from 'components/Heading';
import Link from 'components/Link';
import HeroButtons from 'components/Collection/HeroButtons';

// app modules: props
import CollectionsProps from 'components/Collection/CollectionsProps';
import LoginProps from 'components/Login/LoginProps';

// app modules: services
import assets from 'services/assets';
import { getImageSrc, getImageSrcSet } from 'services/image';
import route from 'services/route';

// app modules: constants
import { CONTENT_TYPES } from 'constants/index';

// assets
import CssCollectionHero from './Hero.scss';

class CollectionHero extends Component {
  static propTypes = {
    collection: CollectionsProps.collection.isRequired,
    user: LoginProps.isRequired,
    tagTitleWhitelist: PropTypes.arrayOf(PropTypes.string),
  };

  static defaultProps = {
    tagTitleWhitelist: [],
  };

  // @todo: move to assets service
  static getCoverImageSource({ customImage, imageFromBook, imageFromContent }) {
    const contentImageSizeOptions = [
      { w: 500, h: 600 },
      { w: 768, h: 800 },
      { w: 1024, h: 1000 },
      { w: 1200, h: 1000 },
      { w: 1600, h: 1000 },
      { w: 2000, h: 1000 },
    ];
    const imageSource = {};
    if (imageFromBook) {
      imageSource.book = {
        src: assets.coverUrl(imageFromBook, {
          resize: 'fit-in',
          width: 300,
          height: 300,
        }),
        srcSet: assets.srcSet(
          imageFromBook,
          [{ w: 300, h: 300, resize: 'fit-in' }, { w: 600, h: 600, resize: 'fit-in' }],
          assets.coverUrl,
        ),
      };
    }

    if (imageFromContent) {
      imageSource.content = {
        src: getImageSrc(imageFromContent, 'original', size => size.w >= 500),
        srcSet: getImageSrcSet(imageFromContent, 'original', size => size.w >= 300),
      };
    }
    if (customImage) {
      const customUrl = customImage.startsWith('/') ? customImage : `hero/${customImage}`;
      imageSource.custom = {
        src: assets.collectionUrl(customUrl, { width: 1000, height: 300, position: 'center' }),
        srcSet: assets.srcSet(
          customUrl,
          contentImageSizeOptions.map(option => ({ ...option, position: 'center' })),
          assets.collectionUrl,
        ),
      };
    }

    if (imageSource.custom && imageSource.custom.src) {
      return {
        source: imageSource.custom.src,
        srcSet: imageSource.custom.srcSet,
        type: CONTENT_TYPES.CONTENT,
      };
    } else if (imageSource.content && imageSource.content.src) {
      return {
        source: imageSource.content.src,
        srcSet: imageSource.content.srcSet,
        type: CONTENT_TYPES.CONTENT,
      };
    } else if (imageFromBook) {
      return {
        source: imageSource.book.src,
        srcSet: imageSource.book.srcSet,
        type: CONTENT_TYPES.BOOK,
      };
    }

    return {};
  }

  constructor(props) {
    super(props);
    this.state = {
      modal: null,
    };
  }

  renderCollectionPrivacyStatus() {
    const { collection, user, tagTitleWhitelist } = this.props;

    if (collection.visibility === 'public' && (!user || user.id !== collection.user_id)) {
      // We are viewing someone else's collection
      if (collection.tags) {
        // Filter out generic tags
        const specificTags = collection.tags.filter(tag => tagTitleWhitelist.includes(tag));
        if (specificTags.length) {
          let upperCaseClass = '';
          if (specificTags[0] === 'neff') {
            upperCaseClass = 'collection-hero__type--uppercase';
          }
          return (
            <Link
              to={`/collections/${specificTags[0]}`}
              className={`collection-hero__type ${upperCaseClass} heading heading--size-32 link link--energized`}
            >
              {specificTags[0].replace('-', ' ')}
            </Link>
          );
        }
        // No specific tags were found, so assume its community
        return (
          <Link
            to="/collections/community"
            className="collection-hero__type heading heading--size-32 link link--energized"
          >
            Community
          </Link>
        );
      }
      // If the collection is not tagged community (perhaps it was censored) users can still share it using the link.
      return <p className="collection-hero__type">Public Collection</p>;
    }
    return (
      <Link
        to="/collections"
        className="collection-hero__type heading heading--size-32 link link--energized"
      >
        Collection
      </Link>
    );
  }

  render() {
    const { collection, user } = this.props;
    const { modal } = this.state;
    const {
      safe_description,
      cover_image,
      custom_hero_image,
      name,
      display_name,
      person_id,
    } = collection;
    const coverImageContent = cover_image || {
      book: {
        image_permission: false,
      },
      primary_image: null,
      instagram_url: null,
    };

    const { source, srcSet, type } = CollectionHero.getCoverImageSource({
      customImage: custom_hero_image,
      imageFromBook: coverImageContent.book.image_name,
      imageFromContent: coverImageContent.primary_image,
    });

    const descriptionWithLineBreaks = safe_description
      ? safe_description.replace(/\n/g, '<br/>')
      : '';

    const bookClass = type === CONTENT_TYPES.BOOK ? 'collection-hero__image--book' : '';

    return (
      <div className="collection-hero">
        {modal ? React.createElement(modal, { collection }) : null}
        {source || srcSet ? (
          <img
            className={`collection-hero__image ${bookClass}`}
            src={source}
            srcSet={srcSet}
            alt=""
          />
        ) : null}
        <div className="collection-hero__wrapper">
          <div className="collection-hero__body">
            <header className="collection-hero__header">
              <div className="collection-hero__col collection-hero__left">
                <section className="collection-hero__section">
                  {this.renderCollectionPrivacyStatus()}
                </section>

                <Heading level={1} className="heading--size-48 margin-right-10">
                  {name}
                </Heading>

                <NoSsr
                  fallback={
                    <section className="collection-hero__section collection-hero__description margin-bottom-10">
                      <p>&nbsp;</p>
                    </section>
                  }
                >
                  {display_name && user.id !== collection.user_id ? (
                    <section className="collection-hero__section collection-hero__description margin-bottom-10">
                      <p>
                        created by{' '}
                        <strong>
                          {person_id ? (
                            <Link to={route.person(person_id)}>{display_name}</Link>
                          ) : (
                            display_name
                          )}
                        </strong>
                      </p>
                    </section>
                  ) : null}
                </NoSsr>
                <div className="collection-hero__dash" />
                <div
                  className="collection-hero__description"
                  dangerouslySetInnerHTML={{ __html: descriptionWithLineBreaks }}
                />
              </div>
              <div className="collection-hero__col collection-hero__right">
                <HeroButtons collection={collection} imageSrc={source} imageSrcSet={srcSet} />
              </div>
            </header>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  tagTitleWhitelist: state.config.collections.tagTitleWhitelist,
});

const mapDispatchToProps = {
  openModal,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssCollectionHero)(CollectionHero));
